import { ChipType } from 'components/@extended/StatusChip';
import { format } from 'date-fns';
import { ComplianceBlockActiveRuleDto } from 'types/compliance';
import {
    BlockedActionTypeCode,
    BlockSourceType,
    ContactDto,
    ManualBlockFormState,
    OverrideFormState,
    PartyBlockDto,
    PartyBlockingFormState,
    PartyBlockWriteDto,
} from 'types/contacts';

const buildManualState = (row: PartyBlockDto | undefined): ManualBlockFormState => ({
    enabled: !!row && !row.isIgnored,
    changeReason: '',
});

/**
 * Build the form's initial blocking state from the contact's current party_block rows. The form
 * uses a fixed schema (manualCases / manualTimecards + overrides keyed by `r{id}`) so every path
 * any control watches exists in defaultValues — see PartyBlockingFormState's doc comment.
 */
export const blocksToForm = (blocks: PartyBlockDto[] | undefined | null): PartyBlockingFormState => {
    const list = blocks ?? [];
    const overrides: Record<string, OverrideFormState> = {};
    for (const b of list) {
        if (b.sourceType === 'MANUAL') continue; // not represented under overrides
        overrides[`r${b.id}`] = {
            isIgnored: b.isIgnored,
            blockedFrom: b.blockedFrom,
            changeReason: '',
        };
    }
    return {
        manualCases: buildManualState(
            list.find((b) => b.sourceType === 'MANUAL' && b.blockedActionTypeCode === 'CASES')
        ),
        manualTimecards: buildManualState(
            list.find((b) => b.sourceType === 'MANUAL' && b.blockedActionTypeCode === 'TIMECARDS')
        ),
        // Pre-emptive exemption is only meaningful before a row of that source/axis exists. Once
        // one does, the user manages it through the `overrides` path; these fields stay empty.
        debtTimecardsExempt: { isIgnored: false, blockedFrom: null, changeReason: '' },
        complianceCasesExempt: { isIgnored: false, blockedFrom: null, changeReason: '' },
        complianceTimecardsExempt: { isIgnored: false, blockedFrom: null, changeReason: '' },
        complianceAmlTimecardsExempt: { isIgnored: false, blockedFrom: null, changeReason: '' },
        overrides,
    };
};

export const formKeyForRow = (row: PartyBlockDto): string => `r${row.id}`;

/**
 * Convert the form's blocking state into the wire payload. Walks the contact's existing blocks
 * to know which MANUAL rows already exist (for id assignment / DELETE inference) and which
 * COMPLIANCE/DEBT rows must be re-sent so the BE doesn't see them as missing.
 */
export const buildBlocksPayload = (
    blocking: PartyBlockingFormState,
    existing: PartyBlockDto[] | undefined | null
): PartyBlockWriteDto[] => {
    const list = existing ?? [];
    const out: PartyBlockWriteDto[] = [];

    const manualAxes: Array<{ axis: 'CASES' | 'TIMECARDS'; state: ManualBlockFormState }> = [
        { axis: 'CASES', state: blocking.manualCases },
        { axis: 'TIMECARDS', state: blocking.manualTimecards },
    ];
    for (const { axis, state } of manualAxes) {
        if (!state.enabled) continue; // absent ⇒ BE infers DELETE (if a row exists)
        const existingRow = list.find((b) => b.sourceType === 'MANUAL' && b.blockedActionTypeCode === axis);
        out.push({
            ...(existingRow ? { id: existingRow.id } : {}),
            sourceType: 'MANUAL',
            blockedActionTypeCode: axis,
            isIgnored: false,
            blockedFrom: null,
            blockedUntil: null,
            ...(state.changeReason ? { changeReason: state.changeReason } : {}),
        });
    }

    for (const row of list) {
        if (row.sourceType === 'MANUAL') continue;
        const override = blocking.overrides?.[`r${row.id}`];
        // Use override values as a unit when present — `??` would treat an explicit
        // `blockedFrom: null` (user cleared the override) as "missing" and resurrect the
        // server row's date, sending a stale {isIgnored: false, blockedFrom: <old date>}.
        out.push({
            id: row.id,
            sourceType: row.sourceType,
            blockedActionTypeCode: row.blockedActionTypeCode,
            isIgnored: override ? override.isIgnored : row.isIgnored,
            blockedFrom: override ? override.blockedFrom : row.blockedFrom,
            blockedUntil: row.blockedUntil,
            ...(override?.changeReason ? { changeReason: override.changeReason } : {}),
        });
    }

    // Pre-emptive exemptions: emit a no-id write per (source, axis) when no real row exists yet
    // AND the user expressed intent (isIgnored=true or a blockedFrom date). Once a real row
    // exists, the user manages exemption through the override path above. blockedUntil stays
    // null — BE rejects it on proactive writes.
    const proactiveExempts: Array<{
        source: 'DEBT' | 'COMPLIANCE';
        axis: BlockedActionTypeCode;
        exempt: OverrideFormState;
    }> = [
        { source: 'DEBT', axis: 'TIMECARDS', exempt: blocking.debtTimecardsExempt },
        { source: 'COMPLIANCE', axis: 'CASES', exempt: blocking.complianceCasesExempt },
        { source: 'COMPLIANCE', axis: 'TIMECARDS', exempt: blocking.complianceTimecardsExempt },
        { source: 'COMPLIANCE', axis: 'AML_TIMECARDS', exempt: blocking.complianceAmlTimecardsExempt },
    ];
    for (const { source, axis, exempt } of proactiveExempts) {
        const existing = list.find((b) => b.sourceType === source && b.blockedActionTypeCode === axis);
        if (existing) continue;
        if (!exempt.isIgnored && !exempt.blockedFrom) continue;
        out.push({
            sourceType: source,
            blockedActionTypeCode: axis,
            isIgnored: exempt.isIgnored,
            blockedFrom: exempt.blockedFrom,
            blockedUntil: null,
            ...(exempt.changeReason ? { changeReason: exempt.changeReason } : {}),
        });
    }

    // Always return the full desired list — an empty array is a meaningful "delete everything"
    // signal. Returning null here would make the BE skip applyDesiredBlocks entirely (it treats
    // null as "no change"), so clearing the last manual block would never persist.
    return out;
};

export type SourceBlockStatus = 'blocked' | 'not-blocked';

export interface SourceRowState {
    id: number | null;
    /** Row exists on the BE for this source/axis. */
    present: boolean;
    /** Row is currently enforcing (not ignored, in active window). */
    enforcing: boolean;
    /** Row is set up but its blockedFrom is in the future. */
    planned: boolean;
    isIgnored: boolean;
    blockedFrom: string | null;
    blockedUntil: string | null;
    gracePeriodDays: number | null;
}

export interface ComplianceSummaryState extends SourceRowState {
    rules: ComplianceBlockActiveRuleDto[];
}

export interface BlockingPolicySummaryState {
    id: 'cases' | 'timecards' | 'aml-timecards';
    titleKey: string;
    overallStatus: SourceBlockStatus;
    manual: SourceRowState;
    compliance: ComplianceSummaryState;
    debt?: SourceRowState;
}

export const parseLocalDate = (dateStr: string): Date => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
};

export const startOfToday = (): Date => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const parseIsoLike = (dateStr: string): Date =>
    /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? parseLocalDate(dateStr) : new Date(dateStr);

export const isRowEnforcing = (row: PartyBlockDto, today: Date = startOfToday()): boolean => {
    if (row.isIgnored) return false;
    if (row.blockedFrom && parseIsoLike(row.blockedFrom) > today) return false;
    if (row.blockedUntil && parseIsoLike(row.blockedUntil) <= today) return false;
    return true;
};

export const isRowPlanned = (row: PartyBlockDto, today: Date = startOfToday()): boolean =>
    !row.isIgnored && !!row.blockedFrom && parseIsoLike(row.blockedFrom) > today;

const EMPTY_ROW: SourceRowState = {
    id: null,
    present: false,
    enforcing: false,
    planned: false,
    isIgnored: false,
    blockedFrom: null,
    blockedUntil: null,
    gracePeriodDays: null,
};

export const findBlockRow = (
    blocks: PartyBlockDto[] | undefined,
    source: BlockSourceType,
    axis: BlockedActionTypeCode
): PartyBlockDto | undefined => blocks?.find((b) => b.sourceType === source && b.blockedActionTypeCode === axis);

export const toRowState = (row: PartyBlockDto | undefined): SourceRowState => {
    if (!row) return EMPTY_ROW;
    return {
        id: row.id,
        present: true,
        enforcing: isRowEnforcing(row),
        planned: isRowPlanned(row),
        isIgnored: row.isIgnored,
        blockedFrom: row.blockedFrom,
        blockedUntil: row.blockedUntil,
        gracePeriodDays: row.gracePeriodDays,
    };
};

export const formatDateValue = (dateStr: string, dateFormat: string): string =>
    format(parseIsoLike(dateStr), dateFormat);

export const getOverallStatusChipType = (status: SourceBlockStatus): ChipType =>
    status === 'blocked' ? 'error' : 'success';

export const getBlockingPolicyReadonlyState = (contact: ContactDto): BlockingPolicySummaryState[] => {
    const activeRules = contact.complianceBlockActiveRules ?? [];
    const blocks = contact.blocks ?? [];

    const buildCompliance = (
        axis: BlockedActionTypeCode,
        ruleFilter: (rule: ComplianceBlockActiveRuleDto) => boolean
    ): ComplianceSummaryState => ({
        ...toRowState(findBlockRow(blocks, 'COMPLIANCE', axis)),
        rules: activeRules.filter(ruleFilter),
    });

    return [
        {
            id: 'cases',
            titleKey: 'contacts.blocking.new-cases',
            overallStatus: contact.isAddingNewCasesBlocked ? 'blocked' : 'not-blocked',
            manual: toRowState(findBlockRow(blocks, 'MANUAL', 'CASES')),
            compliance: buildCompliance('CASES', (r) => r.blocksCases),
        },
        {
            id: 'timecards',
            titleKey: 'contacts.blocking.timecards',
            overallStatus: contact.isAddingTimecardsBlocked ? 'blocked' : 'not-blocked',
            manual: toRowState(findBlockRow(blocks, 'MANUAL', 'TIMECARDS')),
            compliance: buildCompliance('TIMECARDS', (r) => r.blocksTimecards),
            debt: toRowState(findBlockRow(blocks, 'DEBT', 'TIMECARDS')),
        },
        {
            id: 'aml-timecards',
            titleKey: 'contacts.blocking.aml-timecards',
            overallStatus: (contact.isAmlTimecardsBlockActive ?? false) ? 'blocked' : 'not-blocked',
            manual: EMPTY_ROW,
            compliance: buildCompliance('AML_TIMECARDS', (r) => r.blocksAmlTimecards),
        },
    ];
};
